import { useEffect, useMemo, useRef, useState } from "react";
import { II } from "../../components/common/Icons";
import api from "../../services/api";
import HrModal from "./HrModal";
import { useSocket } from "../../context/SocketContext";
import "../../assets/styles/HR/HrCalendarPg.css";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function HrCalendarPg({ readOnly = false }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState({
    title: "",
    date: "",
    type: "event",
    time: "",
    description: ""
  });
  const csvRef = useRef(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const { connected, on, off } = useSocket();

  const demoCsv = "title,date,end_date,type,color,description,scope\nCompany All Hands,2026-03-10,2026-03-10,event,#4338ca,Monthly company-wide all hands meeting,global\nFounders Day,2026-03-21,2026-03-21,holiday,#ef4444,Company holiday for all employees,global";
  const demoRows = demoCsv.split("\n").map((line) => line.split(","));

  const dateKey = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const loadEvents = async (targetMonth = month, targetYear = year) => {
    try {
      const data = await api.getEvents(targetMonth + 1, targetYear);
      const mapped = (data || []).map((e) => ({
        date: String(e.date || "").split("T")[0],
        label: e.title,
        type: e.type || "event",
        time: e.time || "All Day",
        description: e.description || ""
      }));
      setEvents(mapped);
    } catch {
      setEvents([]);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [month, year]);

  useEffect(() => {
    const refresh = () => loadEvents(month, year);
    const onCalendarEvent = () => refresh();
    if (connected) on("calendar:event", onCalendarEvent);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      if (connected) off("calendar:event", onCalendarEvent);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [connected, on, off, month, year]);

  const grid = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const rows = [];
    let week = Array(firstDay).fill(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      week.push(day);
      if (week.length === 7) {
        rows.push(week);
        week = [];
      }
    }
    if (week.length) {
      while (week.length < 7) week.push(null);
      rows.push(week);
    }
    return rows;
  }, [month, year]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (!e.date) return;
      map[e.date] = map[e.date] || [];
      map[e.date].push(e);
    });
    return map;
  }, [events]);

  const selectedEvents = selectedDate ? (eventsByDate[selectedDate] || []) : [];
  const selectedDateObj = selectedDate ? new Date(`${selectedDate}T00:00:00`) : null;
  const today = new Date();
  const isToday = (d) => d
    && today.getFullYear() === year
    && today.getMonth() === month
    && today.getDate() === d;

  const goPrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear((v) => v - 1);
      return;
    }
    setMonth((v) => v - 1);
  };

  const goNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear((v) => v + 1);
      return;
    }
    setMonth((v) => v + 1);
  };

  const createEvent = async () => {
    if (!eventForm.title || !eventForm.date) return;
    try {
      await api.createEvent({
        title: eventForm.title,
        date: eventForm.date,
        type: eventForm.type,
        time: eventForm.time,
        description: eventForm.description
      });
      setShowCreate(false);
      setEventForm({ title: "", date: "", type: "event", time: "", description: "" });
      setSuccessMsg("Event created and saved");
      setTimeout(() => setSuccessMsg(""), 3500);
      await loadEvents(month, year);
    } catch (e) {
      alert(`Error: ${e.message}`);
    }
  };

  const downloadCsv = async () => {
    try {
      const text = await api.downloadCalendarTemplate();
      const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "calendar-events-template.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Template download failed: ${e.message}`);
    }
  };

  const uploadCalendarCsv = async () => {
    const file = csvRef.current?.files?.[0];
    if (!file) {
      alert("Please select CSV file");
      return;
    }
    setCsvLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.bulkUploadCalendarEvents(fd);
      setSuccessMsg(`Calendar CSV uploaded: ${res.insertedCount || 0} events created`);
      setTimeout(() => setSuccessMsg(""), 3500);
      await loadEvents(month, year);
      if (csvRef.current) csvRef.current.value = "";
    } catch (e) {
      alert(`Upload failed: ${e.message}`);
    } finally {
      setCsvLoading(false);
    }
  };

  const upcomingEvents = [...events]
    .filter((e) => {
      const eventDate = new Date(`${e.date}T00:00:00`);
      const todayDate = new Date();
      todayDate.setHours(0, 0, 0, 0);
      return eventDate >= todayDate;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 8);

  return (
    <div>
      <div className="hrcalendar-head">
        <div className="sec hrcalendar-title">Calendar and Events</div>
        {!readOnly && (
          <button className="btn btn-s" onClick={() => setShowCreate(true)}>
            {II("plus", 14)} Create Event
          </button>
        )}
      </div>

      {successMsg && <div className="toast">{II("chk", 16, { c: "#2563eb" })} {successMsg}</div>}

      {!readOnly && (
        <div className="cd hrcalendar-upload-card">
          <div className="cd-h">
            <div className="cd-t">Bulk Calendar Upload</div>
            <div className="cd-s">Upload CSV and publish fixed events to all users</div>
          </div>
          <div className="cd-b">
            <div className="hrcalendar-upload-row">
              <button className="btn-o btn-s" onClick={() => setShowDemo((v) => !v)}>
                {showDemo ? "Hide Demo File" : "View Demo File"}
              </button>
              <button className="btn-o btn-s" onClick={downloadCsv}>Download Sample CSV</button>
              <input ref={csvRef} type="file" accept=".csv,.txt" className="fi hrcalendar-file" />
              <button className="btn btn-s" onClick={uploadCalendarCsv} disabled={csvLoading}>
                {csvLoading ? "Uploading..." : "Upload CSV"}
              </button>
            </div>
            {showDemo && (
              <div className="hrcalendar-demo-wrap">
                <table className="tbl hrcalendar-demo-table">
                  <thead>
                    <tr>{demoRows[0].map((h, idx) => <th key={idx}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {demoRows.slice(1).map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {demoRows[0].map((_, colIdx) => (
                          <td key={colIdx} className="hrcalendar-demo-cell">{row[colIdx] || ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="cd">
        <div className="cd-b">
          <div className="hrcalendar-nav">
            <button className="btn-o btn-s" onClick={goPrevMonth}>{II("left", 14)}</button>
            <div className="hrcalendar-month">{MONTHS[month]} {year}</div>
            <button className="btn-o btn-s" onClick={goNextMonth}>{II("right", 14)}</button>
          </div>

          <table className="hrcalendar-grid">
            <thead>
              <tr>
                {WEEK_DAYS.map((day, i) => (
                  <th key={day} className={`hrcalendar-weekday ${i === 0 || i === 6 ? "is-weekend" : ""}`}>{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((week, wi) => (
                <tr key={wi}>
                  {week.map((d, di) => {
                    const weekend = di === 0 || di === 6;
                    const dKey = d ? dateKey(d) : "";
                    const dayEvents = dKey ? (eventsByDate[dKey] || []) : [];
                    const selected = dKey && selectedDate === dKey;
                    return (
                      <td key={di} className="hrcalendar-cell-wrap">
                        {d ? (
                          <button
                            type="button"
                            className={`hrcalendar-cell ${isToday(d) ? "is-today" : ""} ${selected ? "is-selected" : ""} ${weekend ? "is-weekend" : ""}`}
                            onClick={() => setSelectedDate(dKey)}
                          >
                            <div className="hrcalendar-cell-date">{d}</div>
                            <div className="hrcalendar-dots">
                              {dayEvents.slice(0, 4).map((ev, idx) => (
                                <span
                                  key={`${ev.label}-${idx}`}
                                  className={`hrcalendar-dot hrcalendar-dot--${ev.type || "event"}`}
                                  title={ev.label}
                                />
                              ))}
                              {dayEvents.length > 4 && (
                                <span className="hrcalendar-dot-more">+{dayEvents.length - 4}</span>
                              )}
                            </div>
                          </button>
                        ) : (
                          <div className="hrcalendar-empty" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Selected date detail panel - inline, above Upcoming Events */}
          {selectedDate && (
            <div className="hrcalendar-detail-panel">
              <div className="hrcalendar-detail-header">
                <div className="hrcalendar-detail-title">
                  {selectedDateObj
                    ? `${WEEK_DAYS[selectedDateObj.getDay()]}, ${MONTHS[selectedDateObj.getMonth()]} ${selectedDateObj.getDate()}, ${selectedDateObj.getFullYear()}`
                    : ""}
                </div>
                <button className="hrcalendar-detail-close" onClick={() => setSelectedDate(null)}>✕</button>
              </div>
              {selectedEvents.length === 0 ? (
                <div className="hrcalendar-detail-empty">No events on this day.</div>
              ) : (
                <div className="hrcalendar-detail-list">
                  {selectedEvents.map((e, i) => (
                    <div key={`${e.label}-${i}`} className={`hrcalendar-detail-card hrcalendar-detail-card--${e.type || "event"}`}>
                      <div className="hrcalendar-detail-card-left">
                        <span className={`hrcalendar-detail-dot hrcalendar-dot--${e.type || "event"}`} />
                      </div>
                      <div className="hrcalendar-detail-card-body">
                        <div className="hrcalendar-detail-card-title">{e.label}</div>
                        <div className="hrcalendar-detail-card-meta">
                          {e.time && e.time !== "All Day" ? e.time : "All Day"}
                          &nbsp;·&nbsp;
                          <span className={`chip ${e.type === "holiday" ? "chip-dn" : "chip-ip"}`}>{e.type}</span>
                        </div>
                        {e.description && (
                          <div className="hrcalendar-detail-card-desc">{e.description}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="hrcalendar-upcoming">
            <div className="hrcalendar-upcoming-title">Upcoming Events</div>
            {upcomingEvents.length === 0 ? (
              <div className="hrcalendar-empty-upcoming">No upcoming events</div>
            ) : (
              upcomingEvents.map((e, i) => (
                <div key={`${e.date}-${e.label}-${i}`} className="hrcalendar-upcoming-row">
                  <div className="hrcalendar-upcoming-date">{e.date}</div>
                  <div className="hrcalendar-upcoming-name">{e.label}</div>
                  <span className={`chip ${e.type === "holiday" ? "chip-dn" : "chip-ip"}`}>{e.type}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {!readOnly && (
        <HrModal open={showCreate} onClose={() => setShowCreate(false)} title="Create New Event">
          <div className="fg">
            <label className="fl">Event Title *</label>
            <input
              className="fi"
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              placeholder="Event name..."
            />
          </div>
          <div className="fr2">
            <div className="fg">
              <label className="fl">Date *</label>
              <input
                className="fi"
                type="date"
                value={eventForm.date}
                onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
              />
            </div>
            <div className="fg">
              <label className="fl">Time</label>
              <input
                className="fi"
                type="time"
                value={eventForm.time}
                onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
              />
            </div>
          </div>
          <div className="fg">
            <label className="fl">Event Type</label>
            <select
              className="fsel"
              value={eventForm.type}
              onChange={(e) => setEventForm({ ...eventForm, type: e.target.value })}
            >
              <option value="event">Event</option>
              <option value="holiday">Holiday</option>
              <option value="milestone">Milestone</option>
            </select>
          </div>
          <div className="fg">
            <label className="fl">Description</label>
            <textarea
              className="fta"
              value={eventForm.description}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              placeholder="Event description (optional)..."
            />
          </div>
          <div className="hrcalendar-create-actions">
            <button className="btn-o" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn" onClick={createEvent}>Create Event</button>
          </div>
        </HrModal>
      )}
    </div>
  );
}

export default HrCalendarPg;
